import { useId, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '../../lib/cn'

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  hint?: string
  error?: string
  rightSlot?: ReactNode
}

export function Field({ label, hint, error, rightSlot, className, id, required, ...props }: FieldProps) {
  const autoId = useId()
  const fieldId = id ?? props.name ?? autoId
  return (
    <div className={className}>
      {label && (
        <label htmlFor={fieldId} className="gsm-label">
          {label}
          {required && <span className="ml-0.5 text-brand-600" aria-hidden="true">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          id={fieldId}
          aria-required={required || undefined}
          aria-invalid={error ? 'true' : undefined}
          className={cn('gsm-input', error && 'border-brand-500 ring-4 ring-brand-500/10', rightSlot && 'pr-24')}
          {...props}
        />
        {rightSlot && <div className="absolute right-1.5 top-1/2 -translate-y-1/2">{rightSlot}</div>}
      </div>
      {error ? (
        <p className="mt-1 text-xs font-medium text-brand-600">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-ink-400">{hint}</p>
      ) : null}
    </div>
  )
}

export function Checkbox({
  label,
  checked,
  onChange,
}: {
  label: ReactNode
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 py-1.5 text-sm text-ink-700">
      <span
        className={cn(
          'mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-md border transition',
          checked ? 'border-brand-600 bg-brand-600' : 'border-ink-300 bg-white',
        )}
      >
        {checked && (
          <svg viewBox="0 0 12 12" className="h-3 w-3 text-white" fill="none">
            <path d="M2.5 6.2 4.8 8.5 9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="leading-snug">{label}</span>
    </label>
  )
}
